/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';

import {BehaviorSubject, EMPTY, Subscription} from 'rxjs';
import {debounceTime, switchMap, take, takeUntil} from 'rxjs/operators';

import {TranslateService} from '@ngx-translate/core';

import * as moment from 'moment';

import {Destroyable} from '../../../destroyable.component';
import {DATE_MIN, ProfileMode} from '../../../app.constants';
import {EmpFile} from '../../employees/employees.model';
import {EmployeesService} from '../../employees/employees.service';
import {FileChangesSessionService} from '../../files/file-changes-session.service';
import {LocalizationService} from '../../localization/localization.service';
import {format, strToDate} from '../../utils/date';
import {isPresent, isValueConsideredAsEmpty} from '../../utils/lang';
import {DataTransformer} from '../data-transformer/data-transformer';
import {EmpZoneDependencyUtils} from '../emp-zone-dependency.utils';
import {FormCardFieldComponent} from '../form-card/form-card-field/form-card-field.component';
import {EmpZoneDependency, GroupZoneDescriptor, Value, ZoneDescriptor, ZoneValues} from '../form-generator.interfaces';
import {FormGeneratorService} from '../form-generator.service';
import {FormGeneratorUtils} from '../form-generator.utils';

const isTypeOfDate = (type: string) => type === 'DATE' || type === 'MONTH';

const LABELLED_TYPES = ['ENTITY', 'ENTITY_DATA', 'ENTITY_PREFIXED', 'LIST', 'TREE', 'PEOPLE_TREE'];

export interface ConvertToArgumentsOptions {
  keepEmptyValues?: boolean;
  hiddenDisplayValues?: Map<string, string>;
}

@Component({
  selector: 'gp-generated-simple-form',
  providers: [FileChangesSessionService],
  template: `
    <form [formGroup]="form" class="gp-generated-simple-form">
      <div #fieldComponent></div>
    </form>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeneratedSimpleFormComponent extends Destroyable implements OnChanges, OnDestroy {

  @Input()
  public groupZoneDescriptor: GroupZoneDescriptor = null;

  @Input()
  public id: number | string;

  @Input()
  public isEditMode = true;

  @Input()
  public values: ZoneValues = null;

  @Input()
  public readOnly: boolean;

  @Input()
  public nbCol = 4;

  @Output()
  public formStabilised: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public formLoaded: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public formValuesChanged: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  public setDefaultTodayForDate = false;

  @Input()
  public profileMode = ProfileMode.CLASSIC;

  @Input()
  public isAppendToBody = false;

  @Input()
  public deferFileChanges = false;

  public form: UntypedFormGroup;

  public loading: boolean = true;

  // form will be set as loaded after a small timeout. Goal: to stabilise the form
  public loaded: boolean = false;

  @ViewChild('fieldComponent', {read: ViewContainerRef, static: true})
  public fieldComponent: ViewContainerRef;

  public fields: ComponentRef<FormCardFieldComponent>[] = [];

  private formValueChangesSubscription: Subscription;
  private dependencySubscriptions: Subscription[] = [];

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private formBuilder: UntypedFormBuilder,
              private cd: ChangeDetectorRef,
              private formGeneratorService: FormGeneratorService,
              private employeesService: EmployeesService,
              public fileChanges: FileChangesSessionService,
              private empZoneDependencyUtils: EmpZoneDependencyUtils,
              private translate: TranslateService,
              private localization: LocalizationService) {
    super();
    this.fileChanges.enabled = false;
    this.form = formBuilder.group({});
  }

  public ngOnChanges(changes: any) {
    this.fileChanges.enabled = this.deferFileChanges;
    this.form = this.formBuilder.group({});
    this.cd.markForCheck();
    this.loaded = false;
    this.build().then(() => {
      this.formLoaded.emit();
      setTimeout(() => {
        this.loaded = true;
        this.cd.markForCheck();
        if (this.groupZoneDescriptor) {
          this.formStabilised.emit();
        }
      });
    });
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.fields = null;
    this.formValueChangesSubscription?.unsubscribe();
  }


  /**
   * Build the form based on current configuration object.
   *
   * @returns {Promise<void>} a promise that is resolved when the form has been built
   */
  public build(): Promise<any> {
    if (this.groupZoneDescriptor == null) {
      return Promise.resolve();
    }
    if (this.values == null) {
      this.values = DataTransformer
        .createZoneValues(this.groupZoneDescriptor, this.formGeneratorService, [])[0];
    }
    return this.loadFields(this.values)
      .then(() => this.registerDependencies());
  }

  protected loadFields(zoneValues: ZoneValues): Promise<any> {
    this.fields.forEach(ref => ref.destroy());
    this.fields = [];

    if (!this.groupZoneDescriptor) {
      return Promise.resolve();
    }

    const promisesOfLoadedObservables = this.getCardFields(zoneValues)
      .map((field: any) => {
        const componentRef = this.fieldComponent.createComponent(
          this.componentFactoryResolver.resolveComponentFactory(FormCardFieldComponent));

        this.fields.push(componentRef);

        Object.assign(componentRef.instance, {
          groupZoneDescriptor: this.groupZoneDescriptor,
          descriptor: field.descriptor,
          value: field.value,
          editMode: this.isEditMode,
          readOnly: this.readOnly,
          cardColumns: this.nbCol,
          isGeneratedSimpleForm: true,
          profileMode: this.profileMode,
          isAppendToBody: this.isAppendToBody
      });

        return componentRef.instance.loaded.toPromise();
      });

    return Promise.all(promisesOfLoadedObservables).then(fields => {
      if (!fields || fields.length === 0) {
        return;
      }
      const valuesGroup: UntypedFormGroup = this.formBuilder.group({});
      fields.forEach(f => valuesGroup.addControl(f.descriptor.zoneID, this.formBuilder.group({
        type: [f.descriptor.type],
        value: f.control
      })));

      const cardFormGroup: UntypedFormGroup = this.formBuilder.group({
        begindate: [zoneValues ? zoneValues.begindate : null],
        enddate: [zoneValues ? zoneValues.enddate : null],
        isDated: [zoneValues ? zoneValues.isDated : null],
        id: [zoneValues ? zoneValues.id : null],
        groupName: [zoneValues ? zoneValues.groupName : null],
        values: valuesGroup
      });

      this.form = this.formBuilder.group({
        [this.groupZoneDescriptor.groupZoneID]: this.formBuilder.array([cardFormGroup])
      });

      this.formValueChangesSubscription = this.form.valueChanges
        .pipe(
          debounceTime(400)
        )
        .subscribe(value => {
          this.formValuesChanged.emit(value);
          this.fields.forEach(field => field.instance.changeDetectorRef.detectChanges());
        });
    });
  }

  public getModifiedCardsData(): { [card: string]: ZoneValues[] } {
    if (!this.groupZoneDescriptor) {
      return null;
    }

    const groupZoneValues = this.form.value[this.groupZoneDescriptor.groupZoneID] ?
      this.form.value[this.groupZoneDescriptor.groupZoneID][0] : {values: []};

    // FIX retrieve values directly from field components
    groupZoneValues.values = [];
    this.fields.forEach((field: ComponentRef<FormCardFieldComponent>) => {
      const fieldValue = field.instance.value;
      const zoneId = field.instance.descriptor.zoneID;
      groupZoneValues.values[zoneId] = fieldValue;
    });

    this.formGeneratorService.transformZoneValues(groupZoneValues);
    groupZoneValues.values = Object.keys(groupZoneValues.values)
      .filter(item => !isValueConsideredAsEmpty(groupZoneValues.values[item].value))
      .reduce((memo, item) => {
        memo[item] = groupZoneValues.values[item];
        return memo;
      }, {});
    return groupZoneValues;
  }

  public convertToArguments(hiddenValues: Map<any, any> = new Map(),
                            onlyWithDefaultValueAccepted?: boolean,
                            options: ConvertToArgumentsOptions = {}) {
    if (!this.form.valid) {
      return null;
    }

    if (!this.groupZoneDescriptor) {
      return null;
    }

    const groupZoneValues = this.form.value[this.groupZoneDescriptor.groupZoneID] ?
      this.form.value[this.groupZoneDescriptor.groupZoneID][0] : {values: []};

    if (groupZoneValues.id === null) {
      delete groupZoneValues.id;
    }

    if (!this.groupZoneDescriptor.isDated) {
      groupZoneValues.begindate = DATE_MIN;
    }

    if (this.fields) {
      this.fields.forEach(f => {
        if (!isPresent(f.instance.value.value)) {
          f.instance.value.value = '';
        }
        groupZoneValues.values[f.instance.descriptor.zoneID].value = f.instance.value.value;
        if (options.keepEmptyValues) {
          groupZoneValues.values[f.instance.descriptor.zoneID].transId = f.instance.value.transId;
        }
      });
    }

    this.formGeneratorService.transformZoneValues(groupZoneValues);

    const convertedArguments = this.groupZoneDescriptor.zones.map(zone => {
      let value = groupZoneValues && groupZoneValues.values && groupZoneValues.values[zone.zoneID] ?
        groupZoneValues.values[zone.zoneID].value : null;
      if (zone.hidden && isValueConsideredAsEmpty(value)) {
        value = hiddenValues && hiddenValues.has(zone.zoneID) ? hiddenValues.get(zone.zoneID) : null;
        if (isValueConsideredAsEmpty(value) && zone.isRequired && !zone.defaultValue) {
          console.log('WARNING: there is an hidden zone (' + zone.zoneID + ') which is required without value!');
        }
      }
      const argument = {
        name: zone.zoneID,
        type: zone.type,
        transId: zone.transId,
        value: value,
        defaultValue: zone.defaultValue
      };
      if (!options.keepEmptyValues) {
        return argument;
      }
      const valueTransId = groupZoneValues?.values?.[zone.zoneID]?.transId;
      return {
        ...argument,
        value: isValueConsideredAsEmpty(value) ? '' : value,
        displayValue: this.toDisplayValue(zone, value, valueTransId, options.hiddenDisplayValues)
      };
    });

    if (options.keepEmptyValues) {
      return convertedArguments;
    }
    return convertedArguments.filter(argument => {
      if (onlyWithDefaultValueAccepted) {
        return !isValueConsideredAsEmpty(argument.value) || argument.defaultValue;
      } else {
        return !isValueConsideredAsEmpty(argument.value);
      }
    });
  }

  private toDisplayValue(zone: ZoneDescriptor, value: any, valueTransId: string,
                         hiddenDisplayValues: Map<string, string>): string {
    if (isValueConsideredAsEmpty(value)) {
      return '';
    }
    if (zone.hidden) {
      return hiddenDisplayValues?.get(zone.zoneID);
    }
    if (LABELLED_TYPES.includes(zone.type)) {
      return valueTransId ? this.translate.instant(valueTransId) : undefined;
    }
    if (isTypeOfDate(zone.type)) {
      return format(strToDate(value), this.localization.dateFormat);
    }
    if (zone.type === 'BOOLEAN') {
      return this.translate.instant(value ? 'COMMON.yes' : 'COMMON.no');
    }
    return String(value);
  }

  /**
   * The form is displayed if at least one field is not hidden
   */
  public isFormDisplayed(): boolean {
    if (!this.form || !this.groupZoneDescriptor) {
      return false;
    }

    return this.groupZoneDescriptor.zones.find(z => z.hidden === false) !== undefined;
  }

  public getFormGroup(): UntypedFormGroup {
    const formArray = this.form?.controls[this.groupZoneDescriptor?.groupZoneID] as UntypedFormArray;
    return formArray?.controls[0] as UntypedFormGroup;
  }

  public getFormControl(name: string): AbstractControl {
    return this.getFormGroupValues()?.controls[name];
  }

  private getFormGroupValues() {
    return this.getFormGroup()?.controls?.values as UntypedFormGroup;
  }

  private getCardFields(zoneValues: ZoneValues) {
    if (!this.groupZoneDescriptor || !zoneValues) {
      return [];
    }
    return this.groupZoneDescriptor.zones
      .filter(zone => zone.hidden === false)
      .sort((zoneA: ZoneDescriptor, zoneB: ZoneDescriptor) => zoneA.displayorder - zoneB.displayorder)
      .map((descriptor: ZoneDescriptor) => {
        const values = Object.assign({}, zoneValues.values[descriptor.zoneID]);
        if (isTypeOfDate(descriptor.type) && this.hasDefaultValueAndRequired(values, descriptor)) {
          values.value = moment().startOf('day').format('YYYY-MM-DD');
        }
        const control: UntypedFormControl = this.formBuilder.control({
          value: values.value
        }, []);

        if (descriptor.type === 'FILE') {
          this.employeesService.empFiles$
            .pipe(
              take(1),
              switchMap((files: EmpFile[]) => {
                // if not found, try to reload files for the current contractId
                values.file = files.find(f => f.id === values.value);
                if (!values.file && values.value) {
                  const contractId = this.getContractId(descriptor);
                  if (contractId) {
                    return this.employeesService.getEmpFiles(contractId);
                  }
                }
                return EMPTY;
              })
            )
            .subscribe(files => values.file = files.find(f => f.id === values.value));
        }

        return {
          descriptor,
          fieldVisible: true,
          value: values,
          control
        };
      });
  }

  private getContractId(descriptor: ZoneDescriptor): number {
    const contractIdFromValues = this.values.contractId || this.values.empContractId;
    const contractIdFromMetadata = descriptor.metadatas ? descriptor.metadatas.contractId : null;
    return contractIdFromMetadata || contractIdFromValues;
  }

  private hasDefaultValueAndRequired(values: Value, descriptor: ZoneDescriptor): boolean {
    return !values.value && this.setDefaultTodayForDate && descriptor.isRequired;
  }

  private registerDependencies() {
    if (this.readOnly) {
      return;
    }
    this.dependencySubscriptions?.forEach(subscription => subscription.unsubscribe());
    this.dependencySubscriptions = this.empZoneDependencyUtils.registerDependencies(this.groupZoneDescriptor,
      name => this.getFormControl(name),
      controlValue => controlValue?.value,
      target => this.findDependencyTriggers(target)
    )
      ?.map(dependencyObservable => dependencyObservable.pipe(takeUntil(this.componentDestroyed$)))
      .map(dependencyObservable => dependencyObservable.subscribe());
  }

  private findDependencyTriggers(target: string): BehaviorSubject<EmpZoneDependency>[] {
    return this.fields
      ?.map(field => field.instance)
      .filter(instance => instance.descriptor.zoneID === target
        || instance.descriptor.zoneID === FormGeneratorUtils.simplifyZoneName(target))
      .map(instance => instance.dependencyTriggered$);
  }
}
